import { useCallback } from 'react';
import { Stack, Text, Toggle } from '@fluentui/react';
import { useConfigValuesState } from '../../../../hooks/useConfigValue';
import { TimeUserFeatures } from '../../../../api/generated/data-contracts';
import { FunctionOr } from '../../../../types/FunctionOr';
import { unwrap } from '../../../../helpers/unwrap';

export const ManagerApprovalConfig = () => {
    const {
        initialData: {
            configuration: {
                approval,
                autoApprovalOnAutoCreatedUsers,
                setManagersAsDelegate,
                directApproverDelegationEnabled,
                inheritApproverRights,
                approverCustomFiltersEnabled,
            },
        },
        setConfigValue,
    } = useConfigValuesState();

    const setter = useCallback(
        <K extends keyof TimeUserFeatures>(key: K, value: FunctionOr<TimeUserFeatures[K]>) => {
            setConfigValue(config => ({
                ...config,
                [key]: unwrap(value, config[key]),
            }));
        },
        [setConfigValue],
    );

    const setEnforcePeriodClosure = useCallback(
        (checked?: boolean) => {
            setConfigValue(config => ({
                ...config,
                approval: {
                    ...config.approval,
                    allowApprovalOnClosedPeriod: !checked,
                },
            }));
        },
        [setConfigValue],
    );

    return (
        <Stack tokens={{ padding: 20, childrenGap: 16 }}>
            <Stack.Item>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="xLarge">Manager Approval</Text>
                    <Text variant="medium" styles={{ root: { color: '#666666' } }}>
                        Manager Approval covers the standard flow where users submit their reporting period for approval by their manager. Use these settings to
                        control how approvers handle submitted timesheets and closed periods.
                    </Text>
                </Stack>
            </Stack.Item>

            <ToggleWithInfo
                label="Enforce period closure for managers"
                description="Prevents approvers and timesheet owners from changing a period's state once the global period is closed."
                checked={!approval?.allowApprovalOnClosedPeriod}
                onChange={(_, checked) => setEnforcePeriodClosure(checked)}
            />

            <ToggleWithInfo
                label="Auto-approve auto-created users"
                description="Automatically approves submitted periods for users created via auto-creation."
                checked={autoApprovalOnAutoCreatedUsers}
                onChange={(_, checked) => setter('autoApprovalOnAutoCreatedUsers', Boolean(checked))}
            />

            <ToggleWithInfo
                label="Set managers as delegate on auto-created users"
                description="Sets the manager as a delegate for users created via auto-creation."
                checked={setManagersAsDelegate}
                onChange={(_, checked) => setter('setManagersAsDelegate', Boolean(checked))}
            />

            <ToggleWithInfo
                label="Approvers can delegate as their employees"
                description="Allows approvers to enter time on behalf of their direct reports."
                checked={directApproverDelegationEnabled}
                onChange={(_, checked) => setter('directApproverDelegationEnabled', Boolean(checked))}
            />

            <ToggleWithInfo
                label="Inherit approver rights"
                description="Higher-level approvers inherit rights from approvers below them in the hierarchy."
                checked={inheritApproverRights}
                onChange={(_, checked) => setter('inheritApproverRights', Boolean(checked))}
            />

            <ToggleWithInfo
                label="Enable custom filters on approval"
                description="Adds a custom filter panel to the manager approval page."
                checked={approverCustomFiltersEnabled}
                onChange={(_, checked) => setter('approverCustomFiltersEnabled', Boolean(checked))}
            />
        </Stack>
    );
};

const ToggleWithInfo = ({
    label,
    description,
    checked,
    onChange,
}: {
    label: string;
    description: string;
    checked?: boolean;
    onChange: (e: React.MouseEvent<HTMLElement>, checked?: boolean) => void;
}) => (
    <Stack.Item>
        <Stack tokens={{ childrenGap: 4, maxWidth: 400 }}>
            <Toggle label={label} checked={checked} onText="Yes" offText="No" onChange={onChange} styles={{ root: { marginBottom: 0 } }} />
            <Text variant="small" styles={{ root: { color: '#666666' } }}>
                {description}
            </Text>
        </Stack>
    </Stack.Item>
);
